
import "./App.css";

import React, { useState, useEffect, useRef, useCallback } from "react";
import UserContext from "./context/UserContext";
import SocketContext from "./context/SocketContext";
import MainLayout from "./components/MainLayout";
import LobbyContainer from "./components/Lobby/LobbyContainer";
import GameRoomContainer from "./components/GameRoom/GameRoomContainer";
import useUser from "./hooks/useUser";
import { io } from "socket.io-client";
import sanitizeInput from "./hooks/sanitizeInput";
import { getOrCreateUserId } from "./utils/userId";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import analytics from "./utils/analytics";
import AnalyticsConsent from "./components/AnalyticsConsent";
import UIContext from "./context/UIContext";
import SoundProvider from "./utils/sound/SoundProvider";
import LoginPage from "./pages/Login";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import WhatsNew from "./pages/WhatsNew";
import About from "./pages/About";
import HighScores from "./pages/HighScores";
import RebusPreview from "./pages/RebusPreview";
import SlotCarsTrackEditor from "./pages/SlotCarsTrackEditor";
import { Bingo, Canasta, Go, Hangman, FaceFinder, CanvasCraze, Poker, SnakesAndLadders, Wordsearch, WheelOfDestiny, Solitaire, ClosestGuess, SpotTheBall, Territory, ImpulseTrader } from "./pages/games";
import GamesIndex from "./pages/games";
import WhereAmILocationManager from "./components/games/WhereAmILocationManager";

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
analytics.init(MEASUREMENT_ID);

// App-wide constants
export const APP_NAME = "Game for a Laugh";
export const TRACE = false;

export function traceLog(...args) {
  if (TRACE) console.log(...args);
}

function App() {
  // State for modals and UI controls

  const [showCreateRoomModal, setShowCreateRoomModal] = React.useState(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [showGameTypePickerModal, setShowGameTypePickerModal] = useState(false);
  const [systemModal, setSystemModal] = useState(null);
  const [activeLeaveRoomHandler, setActiveLeaveRoomHandler] = useState(null);

  const [, setLobbyRooms] = useState([]);
  const [, setLobbyPlayers] = useState([]);

  const [lobbyData, setLobbyData] = useState({});

  const [gameRoomData, setGameRoomData] = useState({});
  const pendingLaunchRef = useRef(null);

// Detect if we are running inside Discord's iframe
const urlParams = new URLSearchParams(window.location.search);
const isInDiscord = !!urlParams.get("frame_id");

const socketUrl = isInDiscord
  ? "/"                                      // Discord → uses URL Mapping proxy → Render
  : process.env.REACT_APP_SOCKET_URL;

const socketPath = isInDiscord ? "/socketio" : "/socketio";
const serverBaseUrl = (() => {
  const explicitApiUrl = process.env.REACT_APP_API_URL;
  if (explicitApiUrl) {
    return explicitApiUrl.replace(/\/$/, "");
  }

  if (socketUrl && socketUrl !== "/") {
    return socketUrl.replace(/\/$/, "");
  }

  const { protocol, hostname, port, origin } = window.location;
  if (
    (hostname === "localhost" || hostname === "127.0.0.1") &&
    (port === "3000" || port === "")
  ) {
    return `${protocol}//${hostname}:3001`;
  }

  return origin;
})();

  //console.log("Socket.io connecting →", socketUrl, "with path:", socketPath);

  // Socket instance (singleton)
  const [socket] = useState(() =>
    io(socketUrl, {
       auth: { userId: getOrCreateUserId() },
       path: socketPath,
       // Allow fallback when websocket handshake is interrupted by proxies/CDNs.
       transports: ["polling", "websocket"],
       tryAllTransports: true,
       upgrade: true,
       reconnection: true,
       reconnectionAttempts: Infinity,
       reconnectionDelay: 500,
       reconnectionDelayMax: 5000,
       timeout: 20000,
    })
  );

  const [inGameRoom, setInGameRoom] = useState(false);
  const [joinRoomError, setJoinRoomError] = useState("");

  const [gameTypes, setGameTypes] = useState(() => {
    // Try to load from localStorage cache first
    try {
      const cached = localStorage.getItem("gameTypes");
      if (cached) return JSON.parse(cached);
    } catch {}
    return [];
  });

  // context objects
  const [user, setUser] = useUser();


  // First-visit: auto-open profile so the user can choose their own name, unless in launch flow.
  useEffect(() => {
    // Only show profile modal if not in launch flow
    const isLaunch = window.location.pathname.startsWith("/launch/");
    if (user?.isFirstVisit && !isLaunch) {
      setShowUserProfileModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // Toasts for transient notifications (player reconnects, etc.)
  const [toasts, setToasts] = useState([]);

  const addToast = (message, opts = {}) => {
    const id = Math.random().toString(36).slice(2, 9);
    const toast = {
      id,
      message,
      type: opts.type || "info",
      username: opts.username,
      icon: opts.icon,
      color: opts.color,
    };
    setToasts((t) => [...t, toast]);
    // Auto-dismiss after 5s
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, opts.duration || 5000);
    return id;
  };

  const removeToast = (id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  };

  useEffect(() => {
    if (!socket) return;

    const onConnect = () => {
      // socket.auth.userId should be set if you passed it when creating the socket
      const userId = socket.auth?.userId ?? null;
      setUser((prev) => ({ ...prev, socketId: socket.id, userId }));
    };

    const onDisconnect = (reason) => {
      setUser((prev) => ({ ...prev, socketId: null }));
    };

    const onConnectError = () => {
      // Keep noisy handshake errors out of the UI while reconnection fallback continues.
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);

    // in case socket is already connected when effect runs
    if (socket.connected) onConnect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
    };
  }, [socket, setUser]);

  // Set analytics user id when socket connects / user available
  useEffect(() => {
    // analytics.setUser removed: do not set user identifiers in client-side analytics
    // (keeps analytics.js intact for future use if desired)
    try {
  if (analytics.isEnabled()) {
    analytics.setUser(user || getOrCreateUserId(), {
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      lang: navigator.language || '',
      // If you have server-provided country on user object, prefer that.
      country: user?.country || (navigator.language ? navigator.language.split('-')[1] : undefined)
    });
  }
} catch (e) {}
  }, [user]);

  // Route-based pageview tracker component
  function RouteChangeTracker() {
    //const location = useLocation();
    //useEffect(() => {
      // Track full URL including hash fragment
      // const path = window.location.pathname + window.location.search + window.location.hash;
      // const fullUrl = window.location.origin + path;
      // analytics.pageview(path, fullUrl);
    //}, [location]);
    return null;
  }

  // Google Analytics tracking (route-based)
  /*
  // Functions to switch views
  const goToLobby = () => setCurrentView("lobby");
  const goToGameRoom = () => setCurrentView("gameroom");
  const goToShare = () => setCurrentView("share");
  const goToCreateGameRoom = () => setCurrentView("createGameRoom");
  const trackGameChoice = (gameType) => {
    // Send dynamic event to GA4
    ReactGA.event({
      category: "GameChoice", // Group related events (e.g., Ecommerce, Interaction)
      action: "created_game", // What the user did
      label: gameType, // Dynamic value (e.g., product name)
      // Custom parameters for GA4
      item_id: gameType,
      item_name: gameType,
      view: currentView, // Contextual data (e.g., which view the user is on)
    });
  };*/

  // Room ID state, initialize from localStorage if present
  const [roomId, setRoomId] = useState(() => {
    //   try {
    //     return localStorage.getItem("lastRoomId") || null;
    //   } catch {
    //     return null;
    //   }
  });

  const [audioEnabled, setAudioEnabled] = useState(
    typeof user.audioEnabled === "boolean" ? user.audioEnabled : true
  );
  const [darkMode, setDarkMode] = useState(
    typeof user.darkMode === "boolean" ? user.darkMode : false
  );

    // Refresh lobby data on window focus or socket reconnect
    useEffect(() => {
    if (!socket) return;

    // Handler to request fresh lobby data
    const refreshLobby = () => {
      // Only refresh if not in a room
      if (!roomId) {
        socket.emit("getLobbyData");
      }
    };

    // On window/tab focus
    window.addEventListener("focus", refreshLobby);

    // On socket reconnect
    socket.on("connect", refreshLobby);

    return () => {
      window.removeEventListener("focus", refreshLobby);
      socket.off("connect", refreshLobby);
    };
  }, [socket, roomId]);




  /*
  // Send pageview to GA4 whenever the view changes
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: viewToPath[currentView] || "/" });
  }, [currentView]);
  */

  // Sync darkMode/audioEnabled with user and apply dark mode class
  useEffect(() => {
    if (
      user &&
      (user.darkMode !== darkMode || user.audioEnabled !== audioEnabled)
    ) {
      setDarkMode(typeof user.darkMode === "boolean" ? user.darkMode : false);
      setAudioEnabled(
        typeof user.audioEnabled === "boolean" ? user.audioEnabled : true
      );
    }
  }, [user, darkMode, audioEnabled]);

  useEffect(() => {
    const html = document.documentElement;
    if (darkMode) {
      html.classList.add("dark");
    } else {
      html.classList.remove("dark");
    }
  }, [darkMode]);

  // Join Lobby handler (for use in JoinRoomWrapper)
  const getUser = (userObj) => {
    if (socket && userObj && userObj.username) {
      socket.emit("getUser", userObj);
    }
  };

  // Prevent multiple joinLobby emits by tracking if user has already joined lobby

  // Track if the user has already joined the lobby to prevent duplicate emits
  const hasJoinedLobby = useRef(false);

  // Function to join the lobby if conditions are met
  const tryJoinLobby = React.useCallback(() => {
    if (socket && user && user.username && !roomId && !hasJoinedLobby.current) {
      //console.log("calling joinLobby on server :", user);
      socket.emit("joinLobby", user);
      hasJoinedLobby.current = true;
      //goToLobby();
    }
  }, [socket, user, roomId]);

  useEffect(() => {
    tryJoinLobby();
    if (roomId) {
      hasJoinedLobby.current = false; // Reset when entering a room
    }
  }, [user, roomId, socket, tryJoinLobby]);

  const emitLobbyDataSuccess = (lobbyData) => {
    //console.log("emitLobbyDataSuccess lobbyData:", lobbyData);
    setLobbyData(lobbyData);
    setLobbyRooms(lobbyData.lobbyRooms);
    setLobbyPlayers(lobbyData.lobbyPlayers);
    setGameTypes(lobbyData.gameTypes);
    // Cache gameTypes in localStorage
    try {
      localStorage.setItem("gameTypes", JSON.stringify(lobbyData.gameTypes));
    } catch {}
  };

  // Join Room handler (used by RoomsList and Lobby)
  // Prevent rapid duplicate joinRoom emits
  const pendingJoinRoom = React.useRef(null);

  const joinRoom = React.useCallback(
    (id, password, isSpectating) => {
      // If there's already a recent pending join for the same room, ignore
      if (pendingJoinRoom.current && pendingJoinRoom.current === id) return;
      pendingJoinRoom.current = id;
      // Clear pending flag after short timeout to allow retries later
      setTimeout(() => {
        if (pendingJoinRoom.current === id) pendingJoinRoom.current = null;
      }, 2000);

      const payload = {
        roomId: id,
        user: {
          username: user.username,
          color: user.color,
          icon: user.icon,
          flag: user.flag,
          userId: getOrCreateUserId(),
        },
      };
      if (password) payload.password = sanitizeInput(password);
      if (isSpectating) {
        socket.emit("joinAsSpectator", payload);
      } else {
        // Also send userId at top level for host reclaim
        socket.emit("joinRoom", { ...payload, userId: getOrCreateUserId() });
        try {
          localStorage.setItem("lastRoomId", id);
        } catch {}
      }
      // Do not call goToGameRoom() here; wait for server confirmation
    },
    [socket, user]
  );

  const launchRoomFromTemplate = React.useCallback(
    async (launchId, search = "") => {
      if (!launchId) {
        return { ok: false, error: "Missing launch id" };
      }

      const querySuffix = search
        ? (String(search).startsWith("?") ? String(search) : `?${String(search)}`)
        : "";

      let response;
      try {
        response = await fetch(`${serverBaseUrl}/api/launch/${encodeURIComponent(launchId)}${querySuffix}`);
      } catch (error) {
        return { ok: false, error: "Unable to reach launch service" };
      }

      let data = null;
      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (!response.ok || !data?.ok || !data?.launchTemplate) {
        return {
          ok: false,
          error: data?.error || "Launch template not found",
        };
      }

      const launchTemplate = data.launchTemplate;
      return await new Promise((resolve) => {
        pendingLaunchRef.current = {
          id: launchTemplate.id,
          autoStart: !!launchTemplate.autoStart,
          resolve,
        };

        socket.emit("createRoom", {
          gameType: launchTemplate.gameType,
          roomName:
            user?.isFirstVisit && user?.roomName
              ? user.roomName
              : (launchTemplate.roomName || user?.roomName || `${launchTemplate.gameType} Room`),
          user: {
            userId: getOrCreateUserId(),
            username: user.username,
            color: user.color,
            icon: user.icon,
            flag: user.flag,
          },
          allowSpectators: launchTemplate.allowSpectators !== false,
          config: launchTemplate.config || {},
        });
      });
    },
    [serverBaseUrl, socket, user]
  );

  // Attempt auto-rejoin when socket connects (use separate effect so it runs when socket reconnects)
  useEffect(() => {
    if (!socket) return;
    const handleConnect = () => {
      try {
        const reconnectRoomId = roomId || localStorage.getItem("lastRoomId");
        if (reconnectRoomId && reconnectRoomId !== "pending") {
          if (user && user.username) {
            console.debug("App: socket connected - auto-rejoin to", reconnectRoomId);
            joinRoom(reconnectRoomId, undefined, false);
          }
        }
      } catch (e) {}
    };
    socket.on("connect", handleConnect);
    // If already connected, run once now
    if (socket.connected) handleConnect();
    return () => {
      try {
        socket.off("connect", handleConnect);
      } catch (e) {}
    };
  }, [socket, joinRoom, roomId, user]);

  // Retry auto-rejoin when user becomes available or changes (covers case where
  // socket connected before user/profile was loaded)
  useEffect(() => {
    try {
      const reconnectRoomId = roomId || localStorage.getItem("lastRoomId");
      if (reconnectRoomId && reconnectRoomId !== "pending") {
        if (socket && socket.connected && user && user.username) {
          console.debug("App: user available - auto-rejoin to", reconnectRoomId);
          joinRoom(reconnectRoomId, undefined, false);
        }
      }
    } catch (e) {}
  }, [user, socket, roomId, joinRoom]);

  const emitGameRoomDataSuccess = useCallback((gameRoomData) => {
    //console.log("emitGameRoomDataSuccess roomData:", gameRoomData);
    setGameRoomData(gameRoomData);
  }, [setGameRoomData]);

  // Listen for lobbyState event to update local lobby state
  useEffect(() => {
    if (!socket) return;



    socket.on("changeGameTypeSuccess", ({ roomId, newGameType }) => {
      // Track game creation in analytics
      analytics.trackRoomCreated({
        gameType : newGameType,
        roomId,
        userId: user?.userId || getOrCreateUserId(),
        region: user?.country || (navigator.language ? navigator.language.split('-')[1] : undefined)
      });
      
    });

    socket.on("joinLobbySuccess", ({ user }) => {
      // User successfully joined the lobby
      // Track game creation in analytics
      analytics.trackLobbyJoined({
        userId: user?.userId || getOrCreateUserId(),
        region: user?.country || (navigator.language ? navigator.language.split('-')[1] : undefined)
      });
    });

    socket.on("saveUserProfileSuccess", (updatedUser) => {
            analytics.trackUserProfileUpdated({
        userId: updatedUser?.userId || getOrCreateUserId(),
        region: updatedUser?.country || (navigator.language ? navigator.language.split('-')[1] : undefined)
      });
    });

    socket.on("gameFinished", ({ roomId, gameType, playerCount, startedAt, finishedAt }) => {
      analytics.trackGameCompleted({
        gameType,
        roomId,
        userId: user?.userId || getOrCreateUserId(),
        region: user?.country || (navigator.language ? navigator.language.split('-')[1] : undefined),
        durationMs: finishedAt && startedAt ? finishedAt - startedAt : 0,
        startedAt, 
        finishedAt,
        playerCount: playerCount || 0
      });
    });

    socket.on("createRoomError", (error) => {
      let msg = "Failed to create room";
      try {
        if (typeof error === "string") msg = error;
        else if (error?.error) msg = error.error;
        else if (error?.message) msg = error.message;
      } catch (e) {}
      setJoinRoomError(msg);
      if (pendingLaunchRef.current?.resolve) {
        pendingLaunchRef.current.resolve({ ok: false, error: msg });
      }
      pendingLaunchRef.current = null;
    });
    socket.on("joinRoomError", (error) => {
      //console.log("Join room error:", error);
      try {
        let msg = "Failed to join room";
        if (typeof error === "string") msg = error;
        else if (error && typeof error === "object") {
          // handle common shapes: { error: '...' } or { message: '...' }
          if (error.error) msg = error.error;
          else if (error.message) msg = error.message;
          else msg = JSON.stringify(error);
        }
        setJoinRoomError(msg);
      } catch (e) {
        setJoinRoomError("Failed to join room");
      }
    });

    socket.on("createRoomSuccess", ({ roomId, gameType }) => {
      setInGameRoom(true);
      setRoomId(roomId);
      setJoinRoomError("");
      //console.log("User created room:", roomId);
      try {
        localStorage.setItem("lastRoomId", roomId);
      } catch (e) {}
      const pendingLaunch = pendingLaunchRef.current;
      if (pendingLaunch?.resolve) {
        pendingLaunch.resolve({ ok: true, roomId });
      }
      pendingLaunchRef.current = null;
      if (pendingLaunch?.autoStart) {
        setTimeout(() => {
          socket.emit("startGame", { roomId });
        }, 0);
      }
      // Track game creation in analytics
      analytics.trackRoomCreated({
        gameType,
        roomId,
        userId: user?.userId || getOrCreateUserId(),
        region: user?.country || (navigator.language ? navigator.language.split('-')[1] : undefined)
      });
    });
    socket.on("joinRoomSuccess", ({ roomId, gameType }) => {
      setInGameRoom(true);
      setRoomId(roomId);
      setJoinRoomError("");

      analytics.trackRoomJoined({
        gameType,
        roomId,
        userId: user?.userId || getOrCreateUserId(),
        region: user?.country || (navigator.language ? navigator.language.split('-')[1] : undefined)
      });
      //console.log("User joined room:", roomId);
      // analytics.event removed to disable client-side event tracking
    });

    // Ensure hostLeft is handled at app-level so a modal can be shown even if
    // the GameRoom component has been unmounted by the time the event arrives.
    socket.on("hostLeft", (data) => {
      try {
        const msg =
          typeof data === "string"
            ? data
            : data?.message ||
              "The host has left the game room so it has been closed down";
        setSystemModal({ message: msg, title: "Room closed" });
      } catch (e) {
        setSystemModal({
          message: "The host has left the game room so it has been closed down",
          title: "Room closed",
        });
      }
      // Ensure we show lobby
      setInGameRoom(false);
      setRoomId(null);
      pendingLaunchRef.current = null;
      try {
        localStorage.removeItem("lastRoomId");
      } catch (e) {}
      // analytics.event removed to disable client-side event tracking
    });

    // When server notifies host disconnected, display modal with countdown
    let reclaimInterval = null;
    socket.on("hostDisconnected", (data) => {
      try {
        const msg =
          typeof data === "string"
            ? data
            : data?.message ||
              "Host disconnected — the room will be held for a short time.";
        const reclaimTimeout = data?.reclaimTimeout || 30000;
        const title = data?.title || "Host disconnected";
        // Show modal immediately with initial remaining seconds
        const deadline = Date.now() + reclaimTimeout;
        setSystemModal({ message: msg, title, reclaimDeadline: deadline });
        // analytics.event removed to disable client-side event tracking

        // Update the modal every second to show remaining time
        if (reclaimInterval) clearInterval(reclaimInterval);
        reclaimInterval = setInterval(() => {
          setSystemModal((prev) => {
            if (!prev || !prev.reclaimDeadline) return prev;
            const remaining = Math.max(0, prev.reclaimDeadline - Date.now());
            if (remaining <= 0) {
              // Time elapsed - clear interval and leave modal as-is; server will transfer host or close room
              clearInterval(reclaimInterval);
              reclaimInterval = null;
              return { ...prev, reclaimDeadline: 0 };
            }
            return { ...prev };
          });
        }, 1000);
      } catch (e) {
        setSystemModal({
          message:
            "Host disconnected — the room will be held for a short time.",
          title: "Host disconnected",
          reclaimDeadline: Date.now() + 30000,
        });
        // analytics.event removed to disable client-side event tracking
      }
    });

    // Host reclaimed (host returned)
    socket.on("hostReclaimed", (data) => {
      // Clear any reclaim timers/modal
      setSystemModal(null);
      try {
        if (reclaimInterval) clearInterval(reclaimInterval);
      } catch (e) {}
      reclaimInterval = null;
      // analytics.event removed to disable client-side event tracking
    });

    // Host transferred to another player (server decided new host)
    socket.on("hostTransferred", (data) => {
      // Clear modal and update UI
      setSystemModal(null);
      try {
        if (reclaimInterval) clearInterval(reclaimInterval);
      } catch (e) {}
      reclaimInterval = null;
      // analytics.event removed to disable client-side event tracking
    });

    // Player reconnected notification
    try {
      socket.off("playerReconnected");
    } catch (e) {}
    socket.on("playerReconnected", (info) => {
      try {
        const who = info?.username || info?.userId || "Someone";
        addToast(`${who} reconnected`);
      } catch (e) {}
    });

    socket.on("leaveRoomSuccess", ({ roomId }) => {
      //console.log("User left room:", roomId);
      setInGameRoom(false);
      setRoomId(null);
      pendingLaunchRef.current = null;
      // analytics.event removed to disable client-side event tracking
    });

    socket.on("emitLobbyDataSuccess", (data) => {
      emitLobbyDataSuccess(data);
      // analytics.event removed to disable client-side event tracking
    });
    socket.on("emitGameRoomDataSuccess", emitGameRoomDataSuccess);

    return () => {
      socket.off("joinLobbySuccess");
      socket.off("createRoomSuccess");
      socket.off("joinRoomSuccess");
      socket.off("leaveRoomSuccess");
      socket.off("hostLeft");
      socket.off("hostDisconnected");
      socket.off("saveUserProfileSuccess");
      socket.off("roomJoined");
      socket.off("changeGameTypeSuccess");
      socket.off("playerReconnected");// new
      socket.off("joinRoomError"); // new
      socket.off("createRoomError"); // new
      socket.off("hostReclaimed"); // new
      socket.off("hostTransferred"); // new
      socket.off("gameFinished"); // new
      socket.off("emitLobbyDataSuccess", emitLobbyDataSuccess);
      socket.off("emitGameRoomDataSuccess", emitGameRoomDataSuccess);
      try {
        if (reclaimInterval) clearInterval(reclaimInterval);
      } catch (e) {}
    };
  }, [socket, emitGameRoomDataSuccess, user?.country, user?.userId]);
  //}, [socket, user, addToast, emitLobbyDataSuccess, emitGameRoomDataSuccess, joinRoom]);

  // Refactored functions are below - these should be global events that could be called from anywhere
  // Send updated user info to server so player lists update
  function saveUserProfile(updatedUser) {
    return new Promise((resolve) => {
      if (!socket) {
        setUser(updatedUser);
        resolve({ ok: true, user: updatedUser });
        return;
      }

      socket.emit("saveUserProfile", updatedUser, (response) => {
        if (response?.ok) {
          const nextUser = response.user || updatedUser;
          setUser(nextUser);
          resolve({ ok: true, user: nextUser });
          return;
        }

        resolve({ ok: false, error: response?.error || "Unable to save profile." });
      });
    });
  }

  // Provide user, socket, and settings context to all children
  return (
    <Router>
      <RouteChangeTracker />
      <RoomUrlNormalizer inGameRoom={inGameRoom} />
      <AnalyticsConsent />
      <UserContext.Provider value={{ user, setUser }}>
        <SoundProvider>
          <SocketContext.Provider value={socket}>
            <UIContext.Provider
              value={{
                showUserProfileModal,
                setShowUserProfileModal,
                saveUserProfile,
                showCreateRoomModal,
                setShowCreateRoomModal,
                showGameTypePickerModal,
                setShowGameTypePickerModal,
                gameTypes,
                setGameTypes,
                joinRoom,
                systemModal,
                inGameRoom,
                activeLeaveRoomHandler,
                setActiveLeaveRoomHandler,
                toasts,
                addToast,
                removeToast,
                // Allow child components to return the UI to the lobby
                exitToLobby: () => {
                  setInGameRoom(false);
                  setRoomId(null);
                  try {
                    localStorage.removeItem("lastRoomId");
                  } catch (e) {}
                },
                // Show a system modal with a message
                openSystemModal: (message, title = "") => {
                  setSystemModal({ message, title });
                },
                closeSystemModal: () => setSystemModal(null),
              }}
            >
              <Routes>
                <Route path="/About" element={<MainLayout><About /></MainLayout>} />
                <Route path="/WhatsNew" element={<MainLayout><WhatsNew /></MainLayout>} />
                <Route path="/HighScores" element={<MainLayout><HighScores /></MainLayout>} />
                <Route path="/TermsOfService" element={<MainLayout><TermsOfService /></MainLayout>} />
                <Route path="/PrivacyPolicy" element={<MainLayout><PrivacyPolicy /></MainLayout>} />
                <Route path="/Tools/RebusPreview" element={<MainLayout><RebusPreview /></MainLayout>} />
                <Route path="/Tools/SlotCarsTrackEditor" element={<MainLayout><SlotCarsTrackEditor /></MainLayout>} />
                <Route path="/Tools/WhereAmILocationManager" element={<MainLayout><WhereAmILocationManager enabled={true} /></MainLayout>} />
                <Route path="/login" element={<LoginPage />} />
                {/* Direct join link: /join/:roomId */}
                <Route
                  path="/join/:roomId"
                  element={
                    <JoinRoomWrapper joinRoom={joinRoom} getUser={getUser} />
                  }
                />
                <Route
                  path="/launch/:launchId"
                  element={
                    <MainLayout>
                      {inGameRoom ? (
                        <GameRoomContainer gameRoomData={gameRoomData} />
                      ) : (
                        <LaunchTemplateWrapper
                          launchRoomFromTemplate={launchRoomFromTemplate}
                          getUser={getUser}
                        />
                      )}
                    </MainLayout>
                  }
                />
                {/* Main app route (lobby/game) */}
                <Route
                  path="/*"
                  element={
                    <MainLayout>
                      {/* {content} */}
                      {inGameRoom ? (
                        <>
                          <GameRoomContainer gameRoomData={gameRoomData} />
                        </>
                      ) : (
                        <LobbyContainer
                          lobbyData={lobbyData}
                          joinRoomError={joinRoomError}
                          setJoinRoomError={setJoinRoomError}
                        />
                      )}
                    </MainLayout>
                  }
                />
                {/* Game info pages */}
                <Route path="/Games/Canasta" element={<MainLayout><Canasta /></MainLayout>} />
                <Route path="/Games/Bingo" element={<MainLayout><Bingo /></MainLayout>} />
                <Route path="/Games/Go" element={<MainLayout><Go /></MainLayout>} />
                <Route path="/Games/Hangman" element={<MainLayout><Hangman /></MainLayout>} />
                <Route path="/Games/FaceFinder" element={<MainLayout><FaceFinder /></MainLayout>} />
                <Route path="/Games/CanvasCraze" element={<MainLayout><CanvasCraze /></MainLayout>} />
                <Route path="/Games/Poker" element={<MainLayout><Poker /></MainLayout>} />
                <Route path="/Games/SnakesAndLadders" element={<MainLayout><SnakesAndLadders /></MainLayout>} />
                <Route path="/Games/WheelOfDestiny" element={<MainLayout><WheelOfDestiny /></MainLayout>} />
                <Route path="/Games/WordSearch" element={<MainLayout><Wordsearch /></MainLayout>} />
                <Route path="/Games/Solitaire" element={<MainLayout><Solitaire /></MainLayout>} />
                <Route path="/Games/ClosestGuess" element={<MainLayout><ClosestGuess /></MainLayout>} />
                <Route path="/Games/SpotTheBall" element={<MainLayout><SpotTheBall /></MainLayout>} />
                <Route path="/Games/Territory" element={<MainLayout><Territory /></MainLayout>} />
                <Route path="/Games/ImpulseTrader" element={<MainLayout><ImpulseTrader /></MainLayout>} />
                <Route path="/Games" element={<MainLayout><GamesIndex /></MainLayout>} />
              </Routes>
            </UIContext.Provider>
          </SocketContext.Provider>
        </SoundProvider>
      </UserContext.Provider>
    </Router>
  );
}

function RoomUrlNormalizer({ inGameRoom }) {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!inGameRoom) return;

    if (
      location.pathname.startsWith("/launch/") ||
      location.pathname.startsWith("/join/")
    ) {
      navigate("/", { replace: true });
    }
  }, [inGameRoom, location.pathname, navigate]);

  return null;
}

function JoinRoomWrapper({ joinRoom, getUser }) {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useUser();

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const password = params.get("password");
    const isUserReady = user && user.username && user.icon;
    if (roomId && joinRoom) {
      if (isUserReady) {
        getUser(user);
        joinRoom(roomId, password, false);
        navigate("/");
      }
    }
  }, [roomId, joinRoom, getUser, navigate, user, location]);
}

function LaunchTemplateWrapper({ launchRoomFromTemplate, getUser }) {
  const { launchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useUser();
  const attemptedLaunchRef = useRef(false);
  const [status, setStatus] = useState("Preparing launch...");

  React.useEffect(() => {
    if (!launchId || !launchRoomFromTemplate) return;

    const isUserReady = user && user.username && user.icon;
    if (!isUserReady) {
      setStatus("Preparing player profile...");
      return;
    }
    if (attemptedLaunchRef.current) return;

    attemptedLaunchRef.current = true;
    let cancelled = false;

    getUser(user);
    setStatus("Creating room...");

    launchRoomFromTemplate(launchId, location.search).then((result) => {
      if (cancelled) {
        navigate("/", { replace: true });
        return;
      }
      if (result?.ok) {
        navigate("/", { replace: true });
        return;
      }
      attemptedLaunchRef.current = false;
      navigate("/", { replace: true });
    });

    return () => {
      navigate("/", { replace: true });
      cancelled = true;
    };
  }, [getUser, launchId, launchRoomFromTemplate, location.search, navigate, user]);

  return (
    <div className="mx-auto flex min-h-[40vh] max-w-xl items-center justify-center px-6 py-16 text-center">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Launching game</h1>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{status}</p>
      </div>
    </div>
  );
}

export default App;
